import React, {
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';
import propTypes from 'prop-types';
import { Checkbox } from '../../leaf-components/Checkbox';
import { QuoteContext } from '../QuoteProvider';
import { QuantityContext } from '../../QuantitySelector/QuantityProvider';
import { formatCurrency } from '../../../utilities/financing';
import { AddonSplitfitmentQuantity } from './AddonSplitfitmentQuantity';
import { Tooltip } from '../../Tooltip';
import { productPropTypes } from '../productPropTypes';
import addonConstants from './addonConstants';

const {
  hazardWarrantyId,
  hazardWarrantyMinPrice,
  hazardWarrantyPercentage,
} = addonConstants;

const roundToTwoPlaces = (number) => Math.round(number * 100) / 100;

// Price is 18% of the tire price no less than 19.99
const calculatePrice = (tireBasePrice) => (
  roundToTwoPlaces(Math.max((tireBasePrice * hazardWarrantyPercentage), hazardWarrantyMinPrice))
);

const HazardWarranty = ({
  product,
  product2,
  pricing,
  isSplitFitment,
}) => {
  const { setAddons } = useContext(QuoteContext);
  const { quantities } = useContext(QuantityContext);
  const [ready, setReady] = useState(false);
  const [checked, setChecked] = useState(false);
  const [total, setTotal] = useState(0);

  const getPrice = useCallback((p) => (
    p?.sku ? calculatePrice(pricing[p.sku]) : 0
  ), [pricing]);

  const getTotalPrice = useCallback((qtyFront, qtyRear) => (
    (getPrice(product) * qtyFront) + (getPrice(product2) * qtyRear)
  ), [getPrice, product, product2]);

  useEffect(() => {
    const { qtyFront, qtyRear } = quantities;

    setTotal(
      getTotalPrice(qtyFront, qtyRear),
    );

    const hazardWarranty = [
      {
        id: hazardWarrantyId,
        free: false,
        checked,
        type: 'ADDON',
        unitPrice: getPrice(product),
        subs: [],
        description: '',
        disabled: false,
        position: 0,
        name: `${isSplitFitment ? 'Front ' : ''}Road Hazard Warranty`,
        qty: quantities.qtyFront,
      },
    ];

    if (isSplitFitment) {
      hazardWarranty.push({
        id: hazardWarrantyId,
        free: false,
        checked,
        type: 'ADDON',
        unitPrice: getPrice(product2),
        subs: [],
        description: '',
        disabled: false,
        position: 1,
        name: 'Rear Road Hazard Warranty',
        qty: quantities.qtyRear,
      });
    }

    setAddons((addons) => ({
      ...addons,
      hazardWarranty,
    }));
  }, [checked, getPrice, getTotalPrice, isSplitFitment, product, product2, quantities, setAddons]);

  useEffect(() => {
    // force re-render of checkbox on mount to prevent webkit from
    // caching state through history navigation
    setTimeout(() => { setReady(true); }, 80);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="cmp-quote-option__card">
      <div
        className={
          isSplitFitment
            ? 'cmp-quote-option cmp-quote-option--split-fit'
            : 'cmp-quote-option'
        }
      >
        <div className="cmp-quote-option__item">
          { ready && (
            <Checkbox
              id="hazard-warranty"
              label="Road Hazard Warranty"
              size="lg"
              checked={checked}
              onChange={() => setChecked(!checked)}
            />
          )}
          <Tooltip name="road hazard warranty" />
        </div>
        <div className="cmp-quote-option__quantity">
          {
            isSplitFitment ? (
              <AddonSplitfitmentQuantity
                qtyRear={quantities.qtyRear}
                qtyFront={quantities.qtyFront}
                frontPrice={getPrice(product)}
                rearPrice={getPrice(product2)}
              />
            ) : (
              <>
                Quantity:
                {' '}
                {quantities.qtyFront}
              </>
            )
          }
        </div>
        {
          !isSplitFitment && (
            <div className="cmp-quote-option__unit-price">
              Unit Price:
              {' '}
              {formatCurrency(getPrice(product))}
            </div>
          )
        }
        <div
          className={
            checked
              ? 'cmp-quote-option__total cmp-quote-option__total--selected'
              : 'cmp-quote-option__total'
          }
        >
          {formatCurrency(total)}
        </div>
      </div>
    </div>
  );
};

HazardWarranty.propTypes = {
  product: productPropTypes,
  product2: productPropTypes,
  pricing: propTypes.shape({
    isValidPrice: propTypes.bool,
    pricingGroup: propTypes.string,
  }),
  isSplitFitment: propTypes.bool,
};

export { HazardWarranty };
